import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import FieldDropdownComponent from "./fieldDropdownComponent";
import MultiSelectDropdownComponent from "./multiselectDropdownComponent";
import { DateComponent, InputComponent, BooleanDropdown, DropdownComponent } from "./fieldInputComponent";
import {
  getOperatorList,
  createEmptyExpression,
  getDefaultValueForField,
  handleRenderValidation,
  getDefaultExpressionData,
  getOperatorListValue,
  createEmptyGroup,
  isSelectAllId,
  addToCascadeContext,
  flatId,
} from "./filterUtils";
import { useDispatch } from "react-redux";
import CustomDropdown from "src/components/customDropdown";
import { LoadingPanel } from "src/components/loadingPanel";
import { setNewMessage } from "src/redux/actions/setting-actions";

const RenderGroup = React.memo(function RenderGroup({
  group,
  onChange,
  parentOnRemove,
  parentIndex,
  isRoot,
  filterColumns,
  originalFilterColumns,
  operatorList,
  eventTypeFields = { eventTypeValue: {}, eventTypeData: [] },
  setEventTypeFields = () => {},
  dateFields = {},
  setDateFields = () => {},
  fieldConfigMap,
  onFieldSpecificAction,
  onEventTypeChange = () => {},
  onDateFieldChange = () => {},
  module,
  appliedFilterQuery,
  gridHistoryId,
  saveQueryObj = {},
  setFormFields,
  formFields,
  dbObjName,
  eventTypeFieldList = [],
  errObj,
  groupCounter,
  setGroupCounter,
  expressionCounter,
  setExpressionCounter,
  hasParsedRef,
  actions,
  setTopFilters = () => {},
  topFilters = {},
  setIsResetTopFilters = () => {},
  isResetTopFilters = false,
  setIsChange,
  setErrObj,
  setIsEventTypeChange = () => {},
  isEventTypeChange = false,
  mainLogic,
  setMainLogic,
  isFieldChange,
  setIsFieldChange,
  setIsChangeFilter = () => {},
  isChangeFilter = false,
  markCascadeCalled = () => {},
  onRemoveExpression = () => {},
  externalCascadeLoadingSet,
  lockedFieldsRef,
  onFieldFetchEnd = () => {},
  resetCascadesSignal = 0,
  cancelCascadesRef = null,
  toggleFieldDefs = [],
  toggleValues = {},
  onToggleChange = () => {},
  requiredFields = null,
  onCascadeActiveChange = () => {},
  lockedExpressionField = null,
}) {
  const dispatch = useDispatch();
  // Tracks fields currently owned by a running cascade IIFE.
  const cascadeLockedFields = React.useRef(new Set());
  // Per-field loading state — drives spinners while a cascade fetch is in-flight.
  const [cascadeLoadingFields, setCascadeLoadingFields] = React.useState(new Set());
  // Generation counter — incremented on cancel so in-flight IIFEs bail early.
  const cascadeGenRef = React.useRef(0);

  // Always-current ref used inside async cascade IIFEs where closure group is stale.
  const groupRef = React.useRef(group);
  groupRef.current = group;

  const cancelCascades = React.useCallback(() => {
    cascadeGenRef.current++;
    cascadeLockedFields.current.clear();
    setCascadeLoadingFields(new Set());
  }, []);

  if (cancelCascadesRef) {
    cancelCascadesRef.current = cancelCascades;
  }

  React.useEffect(() => {
    cancelCascades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetCascadesSignal]);

  // Merge internal cascade loading with external (getDataById) loading.
  const mergedCascadeLoadingSet = React.useMemo(() => {
    const hasInternal = cascadeLoadingFields.size > 0;
    const hasExternal = externalCascadeLoadingSet?.size > 0;
    if (!hasInternal && !hasExternal) return cascadeLoadingFields;
    if (!hasExternal) return cascadeLoadingFields;
    if (!hasInternal) return externalCascadeLoadingSet;
    return new Set([...cascadeLoadingFields, ...externalCascadeLoadingSet]);
  }, [cascadeLoadingFields, externalCascadeLoadingSet]);
  // Notify parent when cascade loading transitions between active / idle.
  const prevCascadeActiveRef = React.useRef(false);
  React.useEffect(() => {
    const isActive = mergedCascadeLoadingSet.size > 0;
    if (isActive !== prevCascadeActiveRef.current) {
      prevCascadeActiveRef.current = isActive;
      onCascadeActiveChange(isActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedCascadeLoadingSet.size]);

  const [operatorListData, setOperatorListData] = React.useState([]);
  const [loadingState, setLoadingState] = React.useState(false);
  const [localLogic, setLocalLogic] = React.useState(group.logic || "and");
  const [dropdownOpen, setDropdownOpen] = React.useState({ fieldDropdown: false, operatorDropdown: false });

  const dropdownRef = React.useRef(null);
  const wrapperRef = React.useRef(null);

  const fieldDropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen.operatorDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen((prev) => ({
          ...prev,
          operatorDropdown: false,
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen.operatorDropdown]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen.fieldDropdown && fieldDropdownRef.current && !fieldDropdownRef.current.contains(event.target)) {
        setDropdownOpen((prev) => ({
          ...prev,
          fieldDropdown: false,
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen.fieldDropdown]);

  // Sync with prop changes
  React.useEffect(() => {
    if (group.logic !== undefined) {
      setLocalLogic(group.logic);
    }
  }, [group.logic]);

  const handleItemChange = React.useCallback(
    (item, idx) => {
      setIsChangeFilter(true);
      setIsChange(true);
      setIsFieldChange(true);
      const currentItem = group.items[idx];
      const newItems = [...group.items];

      const fieldChanged = currentItem?.value?.field !== item?.value?.field;

      const operatorChanged = currentItem?.operator?.operator_key !== item?.operator?.operator_key;

      if (
        fieldChanged &&
        item?.value?.field &&
        selectedFieldsInGroup.includes(item.value.field) &&
        item.value.field !== currentItem?.value?.field
      ) {
        dispatch(
          setNewMessage({
            message: "This field is already used. Choose a different field.",
            type: "Error",
            show: true,
          })
        );
        return;
      }

      if ((fieldChanged || operatorChanged) && item.value?.field) {
        newItems[idx] = {
          ...item,
          inputValue: {
            field: item.value.field,
            value: getDefaultValueForField(
              item.value.type,
              fieldConfigMap?.[item?.value?.field]?.dataItemKey || "",
              item.operator.operator_value,
              item.value.dropDownControl
            ),
          },
        };
      } else {
        newItems[idx] = item;
      }
      if (!fieldChanged && !operatorChanged) {
        const valueToValidate = newItems[idx]?.inputValue?.value ?? "";
        const validationKey = newItems[idx]?.value?.type;
        if (validationKey && (item.value.isRequired || item.value.dropDownControl)) {
          const isEmptyDropdown =
            item.value.dropDownControl &&
            (valueToValidate == null ||
              valueToValidate === "" ||
              (Array.isArray(valueToValidate) && valueToValidate.length === 0));
          const validationResult = isEmptyDropdown
            ? { error: true, errorMsg: "Value is required" }
            : handleRenderValidation(valueToValidate, {
                key: validationKey,
                errorMsg: item?.value?.required_msg,
                operator_value: item?.operator?.operator_value,
                field: item?.value?.field,
                dropDownControl: item?.value?.dropDownControl,
              });
          newItems[idx] = {
            ...newItems[idx],
            errorInfo: validationResult,
          };
        }
      } else if (operatorChanged) {
        newItems[idx] = {
          ...newItems[idx],
          errorInfo: { isError: false, errorMsg: "" },
        };
      }

      // Operator change (e.g. IN ↔ =): auto-fill with Select All (IN/NOT IN) or first real item (=).
      if (operatorChanged && !fieldChanged && item.value?.dropDownControl && actions?.[item.value.field]) {
        const curData = fieldConfigMap?.[item.value.field]?.data ?? [];
        const firstOpt = curData.find((o) => !isSelectAllId(o.id)) ?? curData[0];
        if (firstOpt) {
          const defVal = getDefaultValueForField(
            item.value.type,
            fieldConfigMap?.[item.value.field]?.dataItemKey || "",
            item.operator?.operator_value,
            item.value.dropDownControl
          );
          newItems[idx] = {
            ...newItems[idx],
            inputValue: { ...newItems[idx].inputValue, value: Array.isArray(defVal) ? defVal : firstOpt },
          };
        }
      }

      // Context: items above changed idx only (changed field resets its own value).
      const contextBound = fieldChanged ? idx : idx + 1;
      const cascadeContext = {};
      for (let i = 0; i < contextBound; i++) {
        const it = newItems[i];
        if (!it || it.items) continue;
        const f = it.value?.field;
        const v = it.inputValue?.value;
        if (f && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) {
          cascadeContext[f] = v;
        }
      }

      // True if the newly selected field needs its dropdown data fetched.
      const changedFieldNeedsLoad = fieldChanged && item.value?.dropDownControl && !!actions?.[item.value.field];

      // Stamp both guards before onChange so lazy-load effects don't double-fire.
      const lockFieldForCascade = (field) => {
        markCascadeCalled(field);
        if (fieldConfigMap?.[field]) {
          if (!fieldConfigMap[field]._calledOnce) fieldConfigMap[field]._calledOnce = {};
          fieldConfigMap[field]._calledOnce[field] = true;
        }
      };

      if (changedFieldNeedsLoad) {
        // Mark before onChange so lazy-load guards don't double-fire.
        lockFieldForCascade(item.value.field);
      }

      cascadeGenRef.current++;
      cascadeLockedFields.current.clear();
      setCascadeLoadingFields(new Set());

      // Collect the below-field list before the loop so the async IIFE
      // can iterate the same ordered list.
      const belowFields = [];
      for (let i = idx + 1; i < newItems.length; i++) {
        const belowItem = newItems[i];
        if (!belowItem || belowItem.items) continue;
        const belowField = belowItem.value?.field;
        if (!belowField || !belowItem.value?.dropDownControl) continue;
        // Reset it to its default (Select All for IN/NOT IN, "" —
        // then first item via auto-select — for =) 
        const defaultValue = getDefaultValueForField(
          belowItem.value.type,
          fieldConfigMap?.[belowField]?.dataItemKey || "",
          belowItem.operator?.operator_value,
          belowItem.value.dropDownControl
        );
        newItems[i] = {
          ...belowItem,
          errorInfo: { isError: false, errorMsg: "" },
          inputValue: { ...(belowItem.inputValue || {}), field: belowField, value: defaultValue, text: undefined },
        };
        if (actions?.[belowField]) {
          cascadeLockedFields.current.add(belowField);
          lockFieldForCascade(belowField);
          belowFields.push(belowField);
        }
      }

      const belowCurrentVals = new Map();
      for (const bf of belowFields) {
        const bi = newItems.find((it) => it?.value?.field === bf);
        if (bi) belowCurrentVals.set(bf, bi.inputValue?.value);
      }

      onChange({ ...group, items: newItems });

      const allCascadeFields = [...(changedFieldNeedsLoad ? [item.value.field] : []), ...belowFields];
      if (allCascadeFields.length > 0) {
        setCascadeLoadingFields((prev) => {
          const s = new Set(prev);
          allCascadeFields.forEach((f) => s.add(f));
          return s;
        });
      }
      const iifGen = cascadeGenRef.current;
      (async () => {
        // Step 1: fetch the changed field first so its key is in context.
        if (changedFieldNeedsLoad) {
          // eslint-disable-next-line no-await-in-loop
          const step1Result = await actions[item.value.field](cascadeContext, {});
          // If a reset happened while we were awaiting, bail and release locks.
          if (cascadeGenRef.current !== iifGen) {
            belowFields.forEach((f) => cascadeLockedFields.current.delete(f));
            return;
          }
          setCascadeLoadingFields((prev) => {
            const s = new Set(prev);
            s.delete(item.value.field);
            return s;
          });
          // Unwrap {firstKey, mappedData} shape (specialty/group/provider) vs plain key (comp_plan/period).
          const rawStep1Key =
            step1Result != null && typeof step1Result === "object" && "firstKey" in step1Result
              ? step1Result.firstKey
              : step1Result;
          if (rawStep1Key != null && !isSelectAllId(rawStep1Key)) cascadeContext[item.value.field] = rawStep1Key;
        }
        // Step 2: fetch below fields in order.
        const belowMappedData = new Map();
        for (const belowField of belowFields) {
          // eslint-disable-next-line no-await-in-loop
          const firstKey = await actions[belowField](cascadeContext, {});
          if (cascadeGenRef.current !== iifGen) {
            belowFields.forEach((f) => cascadeLockedFields.current.delete(f));
            return;
          }
          setCascadeLoadingFields((prev) => {
            const s = new Set(prev);
            s.delete(belowField);
            return s;
          });
          // Capture fresh mapped data for the post-cascade value fix.
          if (firstKey != null && typeof firstKey === "object" && Array.isArray(firstKey.mappedData)) {
            belowMappedData.set(belowField, firstKey.mappedData);
          }
          addToCascadeContext(cascadeContext, belowField, belowCurrentVals.get(belowField), firstKey);
        }
        belowFields.forEach((f) => cascadeLockedFields.current.delete(f));

        if (belowMappedData.size > 0) {
          const latestGroup = groupRef.current;
          if (latestGroup?.items?.length) {
            let fixNeeded = false;
            const fixedItems = latestGroup.items.map((it) => {
              if (!it || it.items) return it;
              const f = it.value?.field;
              if (!f || !belowFields.includes(f)) return it;
              // Only single-select operators need this fix; multi-select (IN/NOT IN)
              // values are arrays — Kendo MultiSelect silently drops unmatched items.
              const isMultiOp = it.operator?.operator_value === "IN" || it.operator?.operator_value === "NOT IN";
              if (isMultiOp) return it;
              const val = it.inputValue?.value;
              if (val === null || val === undefined || val === "") return it; // already falsy, auto-select handles it
              const md = belowMappedData.get(f);
              if (!md?.length) return it;
              const valId = String(flatId(val) ?? "");
              if (valId && !md.some((d) => String(d.id) === valId)) {
                // Stale value — replace with first item from fresh cascade data directly.
                fixNeeded = true;
                return { ...it, inputValue: { ...it.inputValue, value: md[0] } };
              }
              return it;
            });
            if (fixNeeded) {
              onChange({ ...latestGroup, items: fixedItems });
            }
          }
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actions,
      dispatch,
      fieldConfigMap,
      group,
      markCascadeCalled,
      onChange,
      setIsChange,
      setIsChangeFilter,
      setIsFieldChange,
    ]
  );

  const selectedFieldsInGroup = React.useMemo(() => {
    return (
      group?.items
        ?.filter((it) => !it.items) // ignore nested groups
        .map((it) => it?.value?.field)
        .filter(Boolean) || []
    );
  }, [group?.items]);

  const getDefaultExpression = React.useCallback(() => {
    let defaultData = null;
    switch (module) {
      case "event":
        let columns = filterColumns.filter((f) => !selectedFieldsInGroup?.includes(f.field));
        onFieldSpecificAction(columns[0]?.field, false);
        defaultData = getDefaultExpressionData(
          filterColumns,
          operatorList,
          columns[0].field,
          getOperatorListValue(columns[0].type, operatorList),
          fieldConfigMap
        );
        break;

      default:
        const availableColumns = (filterColumns || []).filter((f) => !selectedFieldsInGroup?.includes(f.field));
        const firstColumn = availableColumns[0] || filterColumns?.[0];

        if (!firstColumn) {
          defaultData = {
            id: Date.now() + Math.random(),
            items: [createEmptyExpression()],
            logic: "and",
            value: { logic: "and" },
          };
          return defaultData;
        }

        if (!availableColumns.length) {
          defaultData = null;
          return defaultData;
        }

        if (!operatorList || Object.keys(operatorList).length === 0) {
          defaultData = {
            id: Date.now() + Math.random(),
            items: [
              {
                ...createEmptyExpression(),
                value: firstColumn,
              },
            ],
            logic: "and",
            value: { logic: "and" },
          };
          return defaultData;
        }

        onFieldSpecificAction(firstColumn.field, false);
        defaultData = getDefaultExpressionData(
          filterColumns,
          operatorList,
          firstColumn.field,
          getOperatorListValue(firstColumn.type, operatorList),
          fieldConfigMap
        );
        break;
    }
    return defaultData;
  }, [fieldConfigMap, filterColumns, onFieldSpecificAction, operatorList, selectedFieldsInGroup, module]);

  const handleAddExpression = React.useCallback(() => {
    if (expressionCounter < (filterColumns?.length || group?.items?.length || 0)) {
      setIsChange(true);
      setIsChangeFilter(true);
      setIsFieldChange(true);
      setExpressionCounter(expressionCounter + 1);
      const newExpression = getDefaultExpression();
      if (!newExpression) {
        dispatch(
          setNewMessage({
            message: "All available fields are already used.",
            type: "Error",
            show: true,
          })
        );
        return;
      }

      // Build cascade context from all currently selected expressions.
      const cascadeContext = {};
      (group?.items || []).forEach((it) => {
        if (!it || it.items) return;
        const f = it.value?.field;
        const v = it.inputValue?.value;
        if (f && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) {
          cascadeContext[f] = v;
        }
      });

      // Pre-fetch the new expression's dropdown data using cascade context.
      const newItem = newExpression?.items?.[0];
      const newField = newItem?.value?.field;
      if (newField && newItem?.value?.dropDownControl && actions?.[newField]) {
        if (fieldConfigMap[newField]) {
          if (!fieldConfigMap[newField]._calledOnce) fieldConfigMap[newField]._calledOnce = {};
          fieldConfigMap[newField]._calledOnce[newField] = true;
        }
        markCascadeCalled(newField);
        setCascadeLoadingFields((prev) => {
          const s = new Set(prev);
          s.add(newField);
          return s;
        });
        const myGen = cascadeGenRef.current;
        actions[newField](cascadeContext, {}).finally(() => {
          if (cascadeGenRef.current !== myGen) return;
          setCascadeLoadingFields((prev) => {
            const s = new Set(prev);
            s.delete(newField);
            return s;
          });
          onFieldFetchEnd(newField);
        });
      }

      if (Array.isArray(group) && group?.length === 0) {
        let emptyGroup = createEmptyGroup();
        onChange({
          ...group,
          items: [...emptyGroup?.items, newExpression?.items[0]],
        });
      } else {
        onChange({
          ...group,
          items: [...group?.items, newExpression?.items[0]],
        });
      }
    } else {
      dispatch(
        setNewMessage({
          message: `You can add a maximum of ${filterColumns?.length || group?.items?.length || 0} expressions.`,
          type: "Error",
          show: true,
        })
      );
    }
  }, [
    actions,
    dispatch,
    expressionCounter,
    fieldConfigMap,
    getDefaultExpression,
    group,
    markCascadeCalled,
    onChange,
    onFieldFetchEnd,
    setExpressionCounter,
    setIsChange,
    setIsChangeFilter,
  ]);

  const handleRemoveParentGroup = () => {
    // Cancel all in-flight cascade calls before clearing expressions.
    cascadeGenRef.current++;
    cascadeLockedFields.current.clear();
    setCascadeLoadingFields(new Set());

    if (lockedExpressionField) {
      // Keep the locked expression; remove everything else.
      const lockedItem = group.items?.find((it) => it?.value?.field === lockedExpressionField);
      const retainedItems = lockedItem ? [lockedItem] : [];
      setGroupCounter(0);
      setExpressionCounter(retainedItems.length);
      onChange({ ...group, items: retainedItems });
    } else {
      setGroupCounter(0);
      setExpressionCounter(0);
      onChange({ ...group, items: [] });
    }
    setLocalLogic("and");
  };

  const handleRemoveExpression = React.useCallback(
    (idx) => {
      const removedItem = group.items[idx];
      const removedField = removedItem?.value?.field;
      if (lockedExpressionField && removedField === lockedExpressionField) return;
      setIsFieldChange(true);
      setExpressionCounter(expressionCounter - 1);
      // Pre-compute which below fields will be cascade-refreshed so we can clear stale text upfront.
      const belowCascadeFields = new Set();
      for (let i = idx + 1; i < group.items.length; i++) {
        const belowItem = group.items[i];
        if (!belowItem || belowItem.items) continue;
        const bf = belowItem.value?.field;
        if (bf && belowItem.value?.dropDownControl && actions?.[bf]) belowCascadeFields.add(bf);
      }
      const newItems = group.items
        .filter((_, i) => i !== idx)
        .map((it) => {
          if (!it.items && it.value?.field && belowCascadeFields.has(it.value.field) && it.inputValue) {
            return { ...it, inputValue: { ...it.inputValue, text: undefined } };
          }
          return it;
        });
      onChange({ ...group, items: newItems });
      if (removedField) onRemoveExpression(removedField);

      // Re-fetch fields below the removed position with updated context.
      const cascadeContext = {};
      for (let i = 0; i < idx; i++) {
        const it = group.items[i];
        if (!it || it.items) continue;
        const f = it.value?.field;
        const v = it.inputValue?.value;
        if (f && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) {
          cascadeContext[f] = v;
        }
      }

      const belowFields = [];
      for (let i = idx + 1; i < group.items.length; i++) {
        const belowItem = group.items[i];
        if (!belowItem || belowItem.items) continue;
        const belowField = belowItem.value?.field;
        if (!belowField || !belowItem.value?.dropDownControl) continue;
        if (actions?.[belowField] && !cascadeLockedFields.current.has(belowField)) {
          cascadeLockedFields.current.add(belowField);

          markCascadeCalled(belowField);
          if (fieldConfigMap?.[belowField]) {
            if (!fieldConfigMap[belowField]._calledOnce) fieldConfigMap[belowField]._calledOnce = {};
            fieldConfigMap[belowField]._calledOnce[belowField] = true;
          }
          belowFields.push(belowField);
        }
      }

      const belowCurrentVals = new Map();
      for (const bf of belowFields) {
        const bi = group.items.find((it) => it?.value?.field === bf);
        if (bi) belowCurrentVals.set(bf, bi.inputValue?.value);
      }

      if (belowFields.length > 0) {
        setCascadeLoadingFields((prev) => {
          const s = new Set(prev);
          belowFields.forEach((f) => s.add(f));
          return s;
        });
        const removeIifGen = cascadeGenRef.current;
        (async () => {
          const removeMappedData = new Map();
          for (const belowField of belowFields) {
            // eslint-disable-next-line no-await-in-loop
            const firstKey = await actions[belowField](cascadeContext, {});
            if (cascadeGenRef.current !== removeIifGen) {
              belowFields.forEach((f) => cascadeLockedFields.current.delete(f));
              return;
            }
            setCascadeLoadingFields((prev) => {
              const s = new Set(prev);
              s.delete(belowField);
              return s;
            });
            if (firstKey != null && typeof firstKey === "object" && Array.isArray(firstKey.mappedData)) {
              removeMappedData.set(belowField, firstKey.mappedData);
            }
            addToCascadeContext(cascadeContext, belowField, belowCurrentVals.get(belowField), firstKey);
          }
          belowFields.forEach((f) => cascadeLockedFields.current.delete(f));
          // Same stale-value fix as handleItemChange: replace stale single-select
          // values with the first item from the fresh cascade data.
          if (removeMappedData.size > 0) {
            const latestGroup = groupRef.current;
            if (latestGroup?.items?.length) {
              let fixNeeded = false;
              const fixedItems = latestGroup.items.map((it) => {
                if (!it || it.items) return it;
                const f = it.value?.field;
                if (!f || !belowFields.includes(f)) return it;
                const isMultiOp = it.operator?.operator_value === "IN" || it.operator?.operator_value === "NOT IN";
                if (isMultiOp) return it;
                const val = it.inputValue?.value;
                if (val === null || val === undefined || val === "") return it;
                const md = removeMappedData.get(f);
                if (!md?.length) return it;
                const valId = String(flatId(val) ?? "");
                if (valId && !md.some((d) => String(d.id) === valId)) {
                  fixNeeded = true;
                  return { ...it, inputValue: { ...it.inputValue, value: md[0] } };
                }
                return it;
              });
              if (fixNeeded) {
                onChange({ ...latestGroup, items: fixedItems });
              }
            }
          }
        })();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actions,
      expressionCounter,
      fieldConfigMap,
      group,
      lockedExpressionField,
      markCascadeCalled,
      onChange,
      onRemoveExpression,
      setExpressionCounter,
      setIsFieldChange,
    ]
  );

  const countGroupItems = React.useCallback((group) => {
    let exprCount = 0;
    let groupCount = 1; // count this group itself

    if (group?.items) {
      group.items.forEach((item) => {
        if (item.items) {
          // Nested group → recurse
          const { expr, groups } = countGroupItems(item);
          exprCount += expr;
          groupCount += groups;
        } else {
          // Expression
          exprCount += 1;
        }
      });
    }

    return { expr: exprCount, groups: groupCount };
  }, []);
  const handleNestedGroupRemove = React.useCallback(
    (removeIdx) => {
      if (!group?.items) return;

      const removedItem = group.items[removeIdx];

      if (removedItem) {
        if (removedItem.items) {
          // Count everything inside this group
          const { expr, groups } = countGroupItems(removedItem);

          setExpressionCounter((prev) => prev - expr);
          setGroupCounter((prev) => prev - groups);
        } else {
          // Single expression
          setExpressionCounter((prev) => prev - 1);
        }
      }

      const newItems = group.items.filter((_, i) => i !== removeIdx);
      onChange({ ...group, items: newItems });
    },
    [countGroupItems, group, onChange, setExpressionCounter, setGroupCounter]
  );

  const handleParseQuery = async () => {
    if (Object.keys(saveQueryObj).length > 0) {
      setLoadingState(true);
      setFormFields({
        ...formFields,
        filter_name: saveQueryObj.filter_name,
        filter_desc: saveQueryObj.filter_desc,
        isFavorite: saveQueryObj.is_favorite ? true : false,
        version: saveQueryObj?.version || 0,
      });
      onChange({
        ...group,
        logic: saveQueryObj?.filterData?.value?.logic,
        items: saveQueryObj?.filterData?.items,
      });
      setLoadingState(false);
    }
  };

  // // Auto-parse when component mounts
  React.useEffect(() => {
    if (
      appliedFilterQuery &&
      appliedFilterQuery !== "" &&
      filterColumns.length > 0 &&
      !hasParsedRef.current &&
      Object.keys(saveQueryObj).length > 0
    ) {
      hasParsedRef.current = true;
      handleParseQuery(appliedFilterQuery);
    }
  }, [appliedFilterQuery, operatorList, filterColumns, saveQueryObj]);

  return (
    <>
      <div className="filteroverflowauto">
        <div className="filterquerycont">
          <div style={{ display: "flex", alignItems: "center", gap: "0px" }} className="addexpressiongroup">
            <Tooltip openDelay={100} position="top" anchorElement="target" className="text-center">
              <Button
                primary={true}
                className="primarybtn minwidthinherit"
                onClick={handleAddExpression}
                disabled={mergedCascadeLoadingSet.size > 0 || group?.items?.length === filterColumns.length}
                title={
                  mergedCascadeLoadingSet.size > 0
                    ? "Please wait for the current field data to finish loading"
                    : "Click the expression button to add a new expression"
                }
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  className="svgexpression"
                >
                  <path d="M480 64v32H32V64zm-192 96h192v-32H288zm0 128h192v-64H288zm0 96h192v-32H288zm0 64h192v-32H288zM96 352h64v-64h64v-64h-64v-64H96v64H32v64h64z"></path>
                </svg>
                Add Expression
              </Button>
            </Tooltip>
            <Button
              primary={true}
              className="closebtnselected"
              disabled={
                mergedCascadeLoadingSet.size > 0
                  ? true
                  : parentOnRemove && typeof parentIndex === "number"
                    ? false
                    : group?.items === undefined || group?.items?.length === 0
                      ? true
                      : false
              }
              onClick={handleRemoveParentGroup}
              title={
                mergedCascadeLoadingSet.size > 0
                  ? "Please wait for the current field data to finish loading"
                  : "Remove All Expression"
              }
            >
              <i className="fas fa-times"></i>
            </Button>
            {isRoot && requiredFields?.length > 0 && (
              <div className="filter-info-msg requiredinfomsg">
                <i className="fas fa-info-circle" style={{ marginRight: "4px" }}></i>
                <strong>Required:</strong>&nbsp;
                {requiredFields.map((f) => f.label).join(" and ")}
              </div>
            )}
            {toggleFieldDefs.length > 0 && isRoot && (
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                {toggleFieldDefs.map(({ field, title }) => (
                  <div key={field} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <label
                      htmlFor={`toggle_${field}`}
                      style={{ marginBottom: 0, whiteSpace: "nowrap", fontSize: "12px" }}
                    >
                      {title}
                    </label>
                    <Switch
                      id={`toggle_${field}`}
                      checked={Boolean(toggleValues[field])}
                      onChange={(e) => onToggleChange(field, e.value)}
                      onLabel={"ON"}
                      offLabel={"OFF"}
                      size={"medium"}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {loadingState && <LoadingPanel />}
          {group.items?.map((item, idx) => {
            // Generate a more unique key
            const itemKey = item.id ? `item-${item.id}` : `item-${idx}-${Date.now()}`;

            return item.items ? (
              <RenderGroup
                key={itemKey}
                group={item}
                parentIndex={idx}
                parentOnRemove={handleNestedGroupRemove}
                onChange={(newGroup) => handleItemChange(newGroup, idx)}
                isRoot={false}
                filterColumns={filterColumns}
                originalFilterColumns={originalFilterColumns}
                operatorList={operatorList}
                eventTypeFields={eventTypeFields}
                setEventTypeFields={setEventTypeFields}
                dateFields={dateFields}
                setDateFields={setDateFields}
                fieldConfigMap={fieldConfigMap}
                onFieldSpecificAction={onFieldSpecificAction}
                onEventTypeChange={onEventTypeChange}
                onDateFieldChange={onDateFieldChange}
                module={module}
                setGroupCounter={setGroupCounter}
                groupCounter={groupCounter}
                setExpressionCounter={setExpressionCounter}
                expressionCounter={expressionCounter}
                hasParsedRef={hasParsedRef}
                actions={actions}
                setTopFilters={setTopFilters}
                topFilters={topFilters}
                isResetTopFilters={isResetTopFilters}
                setIsResetTopFilters={setIsResetTopFilters}
                setIsChange={setIsChange}
                setErrObj={setErrObj}
                errObj={errObj}
                isEventTypeChange={isEventTypeChange}
                setIsEventTypeChange={setIsEventTypeChange}
                getDefaultExpression={getDefaultExpression}
                setMainLogic={setMainLogic}
                mainLogic={mainLogic}
                isFieldChange={isFieldChange}
                setIsFieldChange={setIsFieldChange}
                isChangeFilter={isChangeFilter}
                setIsChangeFilter={setIsChangeFilter}
                lockedFieldsRef={lockedFieldsRef}
              />
            ) : (
              <div
                key={itemKey}
                className={
                  item?.errorInfo?.error
                    ? "expressiondetails errormsgexpression advancefilterexpression"
                    : item?.value?.dropDownControl &&
                        (item?.operator?.operator_value === "IN" || item?.operator?.operator_value === "NOT IN")
                      ? "expressiondetails dropDownControlExpression advancefilterexpression"
                      : item?.value?.type === "date" &&
                          item?.errorInfo?.error &&
                          (item?.operator?.operator_value === "BETWEEN" ||
                            item?.operator?.operator_value === "NOT BETWEEN")
                        ? "expressiondetails dateBetweenExpression advancefilterexpression"
                        : "expressiondetails advancefilterexpression"
                }
              >
                <FieldDropdownComponent
                  item={item}
                  handleChange={handleItemChange}
                  idx={idx}
                  filterColumns={filterColumns.filter(
                    (f) => !selectedFieldsInGroup.includes(f.field) || f.field === item?.value?.field
                  )}
                  originalFilterColumns={originalFilterColumns}
                  operatorList={operatorList}
                  onFieldSpecificAction={onFieldSpecificAction}
                  wrapperRef={wrapperRef}
                  isReportModule={true}
                  disabled={lockedExpressionField !== null && item?.value?.field === lockedExpressionField}
                />
                <CustomDropdown
                  style={{ width: "200px", marginTop: "20px" }}
                  name={item?.value?.field}
                  data={operatorListData}
                  textField="operator_label"
                  onOpen={(dataValue) => {
                    setOperatorListData(dataValue);
                  }}
                  dataItemKey="operator_key"
                  valueData={item.operator}
                  onChange={(e) => {
                    handleItemChange({ ...item, operator: e }, idx);
                  }}
                  filterable={true}
                  onFilterChange={(dataValue) => {
                    setOperatorListData(dataValue);
                  }}
                  autoClose={false}
                  autoBind={true}
                  isSelectALl={true}
                  dataList={getOperatorList(item?.value?.type, operatorList, item?.value)}
                  disabled={item.value?.type === "boolean"}
                  module={"operator_dropdown"}
                />

                {renderValueInput(
                  item,
                  idx,
                  handleItemChange,
                  fieldConfigMap,
                  actions,
                  eventTypeFields,
                  mergedCascadeLoadingSet,
                  lockedFieldsRef
                )}

                {(!lockedExpressionField || item.value?.field !== lockedExpressionField) && (
                  <Button
                    primary={true}
                    className="closebtn"
                    style={{ marginTop: "20px" }}
                    onClick={() => handleRemoveExpression(idx)}
                    disabled={mergedCascadeLoadingSet.size > 0}
                    title={
                      mergedCascadeLoadingSet.size > 0
                        ? "Please wait for the current field data to finish loading"
                        : "Remove Expression"
                    }
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
});

const renderValueInput = (
  item,
  idx,
  handleItemChange,
  fieldConfigMap,
  actions,
  eventTypeFields,
  cascadeLoadingSet,
  lockedFieldsRef
) => {
  if (item?.value?.dropDownControl) {
    return (
      <>
        {item.operator.operator_value === "IN" || item.operator.operator_value === "NOT IN" ? (
          <MultiSelectDropdownComponent
            item={item}
            handleChange={handleItemChange}
            idx={idx}
            config={fieldConfigMap?.[item?.value?.field]}
            originalData={fieldConfigMap?.[item?.value?.field]?.originalData}
            key={`multiselect-${item?.value?.field}`}
            fieldConfigMap={fieldConfigMap}
            actions={actions}
            eventTypeFields={eventTypeFields}
            cascadeLoadingSet={cascadeLoadingSet}
            lockedFieldsRef={lockedFieldsRef}
          />
        ) : (
          <DropdownComponent
            item={item}
            idx={idx}
            handleChange={handleItemChange}
            config={fieldConfigMap?.[item?.value?.field]}
            originalData={fieldConfigMap?.[item?.value?.field]?.originalData}
            key={`dropdown-${item?.value?.field}`}
            actions={actions}
            eventTypeFields={eventTypeFields}
            cascadeLoadingSet={cascadeLoadingSet}
            lockedFieldsRef={lockedFieldsRef}
          />
        )}
      </>
    );
  }
  switch (item?.value?.type) {
    case "string":
      return <InputComponent item={item} idx={idx} type={"text"} handleChange={handleItemChange} />;
    case "number":
      return (
        <InputComponent
          item={item}
          idx={idx}
          type={item.operator.operator_value === "IN" || item.operator.operator_value === "NOT IN" ? "text" : "number"}
          handleChange={handleItemChange}
        />
      );
    case "date":
      return <DateComponent item={item} idx={idx} handleChange={handleItemChange} />;
    case "boolean":
      return <BooleanDropdown item={item} idx={idx} handleChange={handleItemChange} />;
    default:
      return <InputComponent item={item} idx={idx} type={"text"} handleChange={handleItemChange} />;
  }
};

export default RenderGroup;
